import { useState } from 'react';
import { FormikProps } from 'formik';
import classNames from 'classnames';
import { Drawer, Flex, Form } from 'antd';
import { MButton, MInput } from '@Components/Base';
import { ReactComponent as FilterIcon } from '@Icons/additional/filter.svg';
import { ReactComponent as CloseCrossIcon } from '@Icons/additional/close-cross.svg';
import { validateSearchValue } from '@Utils/searchValidator';

import styles from './request-search.module.css';
import { MobileDrawer, ViewResolver } from '..';
import { Tags } from './Tags';

interface RequestSearchProps {
  className?: string;
  children?: React.ReactNode;
  isMobile?: boolean;
  onHideSearch?: () => void;
  onSearchSubmit?: (value: string | null) => void;
  isOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  // eslint-disable-next-line
  formik?: FormikProps<any>;
  showTags?: boolean;
  showFilterBtn?: boolean;
  closeBtn?: boolean;
  placeholder?: string;
}

const RequestSearch: React.FC<RequestSearchProps> = ({
  className,
  children,
  isMobile = false,
  onSearchSubmit,
  onHideSearch,
  isOpen,
  onClose,
  onOpen,
  formik,
  showFilterBtn = true,
  showTags = false,
  closeBtn = false,
  placeholder = 'Search Requests...',
}) => {
  const [showClearBtn, setShowClearBtn] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearchSubmit = (value: string): void => {
    const validationError = validateSearchValue(value);
    setSearchError(validationError);

    if (!validationError) {
      onSearchSubmit?.(value);
    }
  };

  const handleSearchClear = (): void => {
    setSearchValue('');
    onSearchSubmit?.(null);
    setShowClearBtn(false);
    setSearchError(null);
  };

  return (
    <Form.Item help={searchError} className={styles.wrapper}>
      <div className={classNames(styles.requestSearch, className)}>
        <div className={styles.searchContent}>
          <Flex gap={10} className="search-input-container">
            <MInput
              searchInput
              value={searchValue}
              onChange={(e) => {
                const { value } = e.target;

                setShowClearBtn(Boolean(value));
                setSearchValue(value);
              }}
              className={styles.noBorder}
              handleSubmit={handleSearchSubmit}
              placeholder={placeholder}
            />
          </Flex>
          <Flex>
            {showClearBtn ? (
              <MButton
                className={styles.crossButton}
                onClick={handleSearchClear}
                icon={<CloseCrossIcon />}
              />
            ) : null}
            {showFilterBtn && (
              <MButton
                className={styles.filterButton}
                onClick={onOpen}
                icon={<FilterIcon />}
              >
                {!isMobile && 'Filter'}
              </MButton>
            )}
            {closeBtn && (
              <MButton
                className={styles.filterButton}
                onClick={onHideSearch}
                icon={<CloseCrossIcon />}
              />
            )}
          </Flex>
          <ViewResolver
            desktop={
              <Drawer
                width={410}
                title="Filters"
                onClose={onClose}
                open={isOpen}
              >
                {children}
              </Drawer>
            }
            mobile={
              <MobileDrawer title="Filters" onClose={onClose} open={isOpen}>
                {children}
              </MobileDrawer>
            }
          />
        </div>
        <Tags formik={formik} showTags={showTags} />
      </div>
    </Form.Item>
  );
};

export default RequestSearch;
